import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LS_KEYS } from './constants';
import { GetUserInfoResponse } from '../services/types/TUserServices';

interface ProjectData {
  uuid: string;
  name: string;
  description: string;
  number: string;
  start_date: string;
  end_date: string;
  company: {
    uuid: string;
    name: string;
  };
  fields: Array<{
    uuid: string;
    name: string;
  }>;
  deposits: Array<{
    uuid: string;
    name: string;
    field: string;
  }>;
  areas: Array<{
    uuid: string;
    name: string;
    deposit: string;
  }>;
  is_completed: boolean;
  current_stage: number;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  userInfo: GetUserInfoResponse | null | any;
  projectData: ProjectData | null;
}

// Функция для безопасного получения данных из localStorage
const getSafeLSData = <T>(key: string): T | null => {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;
    return JSON.parse(item) as T;
  } catch (error) {
    console.error(`Ошибка чтения ${key} из localStorage:`, error);
    localStorage.removeItem(key);
    return null;
  }
};

const initialState: AuthState = {
  accessToken: localStorage.getItem(LS_KEYS.token),
  refreshToken: localStorage.getItem(LS_KEYS.refreshToken),
  isAuthenticated: !!localStorage.getItem(LS_KEYS.token),
  userInfo: getSafeLSData<GetUserInfoResponse>(LS_KEYS.userInfo),
  projectData: getSafeLSData<ProjectData>(LS_KEYS.projectData),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logOut(state) {
      // Очищаем localStorage
      Object.values(LS_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      
      // Очищаем состояние
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.userInfo = null;
      state.projectData = null;
      
      console.log('Все данные удалены из localStorage и Redux');
    },
    
    setTokens(state, action: PayloadAction<{ 
      accessToken: string; 
      refreshToken: string; 
    }>) {
      localStorage.setItem(LS_KEYS.token, action.payload.accessToken);
      localStorage.setItem(LS_KEYS.refreshToken, action.payload.refreshToken);
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      
      console.log('Токены сохранены в localStorage и Redux');
    },
    
    setUserInfo(state, action: PayloadAction<GetUserInfoResponse>) {
      try {
        const userInfoString = JSON.stringify(action.payload);
        localStorage.setItem(LS_KEYS.userInfo, userInfoString);
        state.userInfo = action.payload;
        
        console.log('UserInfo сохранен:', action.payload);
        console.log('В localStorage сохранено:', userInfoString);
      } catch (error) {
        console.error('Ошибка сохранения userInfo:', error);
      }
    },
    
    updateToken(state, action: PayloadAction<{ accessToken: string }>) {
      localStorage.setItem(LS_KEYS.token, action.payload.accessToken);
      state.accessToken = action.payload.accessToken;
      state.isAuthenticated = true;
    },
    
    updateRefreshToken(state, action: PayloadAction<{ refreshToken: string }>) {
      localStorage.setItem(LS_KEYS.refreshToken, action.payload.refreshToken);
      state.refreshToken = action.payload.refreshToken;
    },
    
    setProjectData(state, action: PayloadAction<ProjectData>) {
      try {
        localStorage.setItem(LS_KEYS.projectData, JSON.stringify(action.payload));
        state.projectData = action.payload;
        console.log('ProjectData сохранен:', action.payload);
      } catch (error) {
        console.error('Ошибка сохранения projectData:', error);
      }
    },
    
    updateProjectData(state, action: PayloadAction<Partial<ProjectData>>) {
      if (state.projectData) {
        const updatedProjectData = {
          ...state.projectData,
          ...action.payload
        };
        localStorage.setItem(LS_KEYS.projectData, JSON.stringify(updatedProjectData));
        state.projectData = updatedProjectData;
        
        console.log('ProjectData обновлен:', updatedProjectData);
      }
    },
    
    clearProjectData(state) {
      localStorage.removeItem(LS_KEYS.projectData);
      state.projectData = null;
    },
    
    // Экшены для обновления отдельных частей проекта
    updateProjectBasicInfo(state, action: PayloadAction<{
      name?: string;
      description?: string;
      number?: string;
      start_date?: string;
      end_date?: string;
    }>) {
      if (state.projectData) {
        const updatedProjectData = {
          ...state.projectData,
          ...action.payload
        };
        localStorage.setItem(LS_KEYS.projectData, JSON.stringify(updatedProjectData));
        state.projectData = updatedProjectData;
        
        console.log('Основная информация проекта обновлена:', action.payload);
      }
    },
    
    updateProjectCompany(state, action: PayloadAction<{
      uuid: string;
      name: string;
    }>) {
      if (state.projectData) {
        const updatedProjectData = {
          ...state.projectData,
          company: action.payload
        };
        localStorage.setItem(LS_KEYS.projectData, JSON.stringify(updatedProjectData));
        state.projectData = updatedProjectData;
        
        console.log('Компания проекта обновлена:', action.payload);
      }
    },
    
    updateProjectFields(state, action: PayloadAction<Array<{
      uuid: string;
      name: string;
    }>>) {
      if (state.projectData) {
        const updatedProjectData = {
          ...state.projectData,
          fields: action.payload
        };
        localStorage.setItem(LS_KEYS.projectData, JSON.stringify(updatedProjectData));
        state.projectData = updatedProjectData;
        
        console.log('Месторождения проекта обновлены:', action.payload);
      }
    },
    
    updateProjectDeposits(state, action: PayloadAction<Array<{
      uuid: string;
      name: string;
      field: string;
    }>>) {
      if (state.projectData) {
        const updatedProjectData = {
          ...state.projectData,
          deposits: action.payload
        };
        localStorage.setItem(LS_KEYS.projectData, JSON.stringify(updatedProjectData));
        state.projectData = updatedProjectData;
        
        console.log('Залежи проекта обновлены:', action.payload);
      }
    },
    
    updateProjectAreas(state, action: PayloadAction<Array<{
      uuid: string;
      name: string;
      deposit: string;
    }>>) {
      if (state.projectData) {
        const updatedProjectData = {
          ...state.projectData,
          areas: action.payload
        };
        localStorage.setItem(LS_KEYS.projectData, JSON.stringify(updatedProjectData));
        state.projectData = updatedProjectData;
        
        console.log('Участки проекта обновлены:', action.payload);
      }
    },
    
    updateProjectStage(state, action: PayloadAction<{
      current_stage: number;
      is_completed?: boolean;
    }>) {
      if (state.projectData) {
        const updatedProjectData = {
          ...state.projectData,
          current_stage: action.payload.current_stage,
          is_completed: action.payload.is_completed ?? state.projectData.is_completed
        };
        localStorage.setItem(LS_KEYS.projectData, JSON.stringify(updatedProjectData));
        state.projectData = updatedProjectData;
        
        console.log('Этап проекта обновлен:', action.payload.current_stage);
      }
    },
    
    // Функция для инициализации состояния из localStorage
    initializeAuthState(state) {
      const token = localStorage.getItem(LS_KEYS.token);
      const refreshToken = localStorage.getItem(LS_KEYS.refreshToken);
      const userInfo = getSafeLSData<GetUserInfoResponse>(LS_KEYS.userInfo);
      const projectData = getSafeLSData<ProjectData>(LS_KEYS.projectData);
      
      state.accessToken = token;
      state.refreshToken = refreshToken;
      state.isAuthenticated = !!token;
      state.userInfo = userInfo;
      state.projectData = projectData;
      
      console.log('Состояние auth инициализировано из localStorage:');
      console.log('- isAuthenticated:', !!token);
      console.log('- userInfo:', userInfo);
      console.log('- token exists:', !!token);
    }
  },
});

export const { 
  logOut, 
  setTokens, 
  setUserInfo, 
  updateToken, 
  updateRefreshToken,
  setProjectData,
  updateProjectData,
  clearProjectData,
  updateProjectBasicInfo,
  updateProjectCompany,
  updateProjectFields,
  updateProjectDeposits,
  updateProjectAreas,
  updateProjectStage,
  initializeAuthState
} = authSlice.actions;

export type { ProjectData };
export default authSlice.reducer;